import { ChangeEvent } from 'react';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';

import style from '../Input.module.scss';

import { Currency } from 'chaika-enums';

import { Money } from '@store/models/Money';

import { InputLayout, InputLayoutProps } from '../InputLayout';
import { useInput } from '../hooks/useInput';

export interface MoneyInputProps extends React.InputHTMLAttributes<HTMLInputElement>, InputLayoutProps {
    value: string | number;
    currency?: Currency;
    withoutCurrency?: boolean;
    withoutKopecks?: boolean;
    hideIfEmpty?: boolean;
    dataTest?: string;
    forcePlaceholder?: boolean;
    onChange?(event: ChangeEvent<HTMLInputElement>, name?: string): void;
    onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
    onFocus?(event: React.FocusEvent<HTMLInputElement>): void;
}

export function MoneyInput({
    value = 0,
    currency,
    withoutCurrency,
    withoutKopecks,
    hideIfEmpty,
    leftTitle,
    title,
    titleHint,
    theme,
    modifiers,
    notValidText,
    iconLinkProps,
    isValid,
    isRequired,
    isImportant,
    showValidStatus,
    dataTest,
    forcePlaceholder,
    placeholder,
    onChange,
    onBlur,
    onFocus,
    ...props
}: MoneyInputProps) {
    const { isOnFocus, inputValue, commitValue, handleFocus, handleBlur } = useInput({
        value,
        isControlled: Boolean(onChange),
        name: props.name,
        onBlur,
        onFocus,
    });

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        if (!/^-?\d*\.?\d{0,2}$/.test(event.target.value)) {
            return;
        }

        event.target.value = event.target.value.replace(/^0+([^.])/, '$1');

        if (onChange) {
            onChange(event, props.name);
        }

        commitValue(event.target.value);
    };

    return (
        <InputLayout
            isOnFocus={isOnFocus}
            isEmpty={!inputValue && inputValue !== 0}
            disabled={props.disabled}
            leftTitle={leftTitle}
            title={title}
            titleHint={titleHint}
            theme={theme}
            modifiers={modifiers}
            notValidText={notValidText}
            iconLinkProps={iconLinkProps}
            isValid={isValid}
            isRequired={isRequired}
            isImportant={isImportant}
            showValidStatus={showValidStatus}
        >
            <input
                type="text"
                data-test={dataTest}
                data-testid={dataTest}
                className={style.input}
                {...props}
                value={formatDisplayValue(inputValue, {
                    isOnFocus,
                    hideIfEmpty,
                    currency,
                    withoutCurrency,
                    withoutKopecks,
                })}
                autoComplete={props.autoComplete || 'off'}
                placeholder={title ? (forcePlaceholder ? placeholder : undefined) : placeholder}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
            />
        </InputLayout>
    );
}

interface FormatDisplayValueParams {
    isOnFocus: boolean;
    hideIfEmpty?: boolean;
    currency?: Currency;
    withoutCurrency?: boolean;
    withoutKopecks?: boolean;
}

function formatDisplayValue(
    value: string | number,
    { isOnFocus, hideIfEmpty, currency, withoutCurrency, withoutKopecks }: FormatDisplayValueParams,
): string | number {
    if (isOnFocus) {
        return value;
    }

    if (hideIfEmpty && isEmpty(value) && !isNumber(value)) {
        return '';
    }

    const numericValue = Number(value);

    if (isNaN(numericValue) || withoutCurrency) {
        return value;
    }

    return Money.fromRubles(numericValue, currency).toString({ withoutKopecks });
}
